import { ItemPreferences } from '@admin/models';
import { HttpClient } from '@angular/common/http';
import { ItemResource, Link } from '@ngx-hal/models';
import { deepmerge } from 'deepmerge-ts';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export class ItemPreferencesWebService<Entity extends ItemResource> {
  constructor(private readonly httpClient: HttpClient) {}

  create(link: Link, itemPreferences: ItemPreferences<Entity>): Observable<ItemPreferences<Entity>> {
    localStorage.setItem(`item-preferences.${link.href}`, JSON.stringify(itemPreferences));
    return of(itemPreferences);
  }

  read(link: Link): Observable<ItemPreferences<Entity>> {
    const resource = link.href.split('/').slice(3).shift();
    return forkJoin([
      // default config for technical properties
      of({
        filter: {
          createdBy: {
            _hidden: true,
          },
          createdDate: {
            _hidden: true,
          },
          lastModifiedBy: {
            _hidden: true,
          },
          lastModifiedDate: {
            _hidden: true,
          },
        },
        list: {
          createdBy: {
            _hidden: true,
          },
          createdDate: {
            _hidden: true,
          },
          lastModifiedBy: {
            _hidden: true,
          },
          lastModifiedDate: {
            _hidden: true,
          },
        },
      }),

      // default config for giving resource
      this.httpClient
        .get(`/assets/resources-config/${resource}/${link.href.split('/').pop()}.json`)
        .pipe(
          // There is requirement that id property of child entity should be hidden
          // Filtring the config file and cheking if child entity is present and marking id as hidden property.
          map(
            (config: {
              list?: { [key: string]: Record<string, unknown> };
              filter?: { [key: string]: Record<string, unknown> };
            }) => {
              Object.keys(config?.list ?? {}).map(key => {
                if (config.list && config.filter) {
                  const listConfig = config.list[key];
                  const filterConfig = config?.filter[key];
                  if (listConfig['_isChildEntity']) {
                    listConfig['id'] = {
                      _hidden: true,
                    };
                  }
                  if (filterConfig['_isChildEntity']) {
                    filterConfig['id'] = {
                      _hidden: true,
                    };
                  }
                }
              });
              return config;
            },
          ),
        )
        .pipe(catchError(() => of({}))),

      // user config
      of(JSON.parse(localStorage.getItem(`item-preferences.${link.href}`) ?? 'null') ?? {}),
    ]).pipe(map(configs => deepmerge(...configs)));
  }

  update(link: Link, itemPreferences: ItemPreferences<Entity>): Observable<ItemPreferences<Entity>> {
    return this.create(link, itemPreferences);
  }

  patch(link: Link, itemPreferences: Partial<ItemPreferences<Entity>>): Observable<ItemPreferences<Entity>> {
    return this.read(link).pipe(
      catchError(() => of(undefined)),
      switchMap(response => this.create(link, deepmerge(response, itemPreferences))),
    );
  }

  delete(link: Link): Observable<void> {
    localStorage.removeItem(`item-preferences.${link.href}`);
    return EMPTY;
  }
}
